// Parsing PURO dell'etichetta luogo (`place_label`) per il dettaglio foto.
//
// `place_label` è un singolo campo testo libero, spesso (ma non sempre) il
// `display_name` di Nominatim catturato dalla ricerca luogo. Nominatim ordina
// le componenti dal PIÙ SPECIFICO al PIÙ GENERALE (POI, via, zona, …, città,
// regione, CAP, paese). Sfruttiamo questo ORDINE — non una classificazione dei
// token, che da stringa non sarebbe affidabile a livello globale.
//
// Place-1 (UI-only, nessun dato strutturato, nessuna migration): mostriamo un
// "primary" (token più specifico) + un context compatto (ultimi 1–2 token utili,
// tipicamente città/regione + paese), nascondendo il CAP e i livelli intermedi
// rumorosi. La stringa completa resta disponibile (title/accessibilità).
// Le VERE icone per-tipo (quartiere/città/paese) richiederebbero componenti
// address strutturate → batch futuro (Place-2).

export interface ParsedPlace {
  /** Token più specifico (sempre presente se l'input non è vuoto). */
  primary: string;
  /** Context compatto: 0–2 token (tipicamente città/regione + paese). */
  context: string[];
  /** Stringa originale completa (per title/tooltip). */
  full: string;
}

// CAP/ZIP puramente numerici (IT, US, …). Gli alfanumerici (UK/CA) si tengono:
// meglio mostrare un token in più che scartare per errore un luogo reale.
const POSTAL_RE = /^[0-9][0-9\s-]*$/;

// Quanti token di context mostrare al massimo (oltre al primary).
const MAX_CONTEXT = 2;

/**
 * Parsa `place_label` in elementi UI. Ritorna `null` se vuoto/assente.
 * Tollerante: testo libero senza virgole → `{ primary: <tutto>, context: [] }`.
 */
export function parsePlaceLabel(raw: string | null | undefined): ParsedPlace | null {
  if (!raw) return null;
  const full = raw.trim();
  if (!full) return null;

  const tokens = full.split(",").map((t) => t.trim()).filter(Boolean);
  // Testo libero / singolo elemento: mostralo così com'è.
  if (tokens.length <= 1) return { primary: full, context: [], full };

  // Droppa i CAP e deduplica (case-insensitive) preservando l'ordine.
  const seen = new Set<string>();
  const clean: string[] = [];
  for (const t of tokens) {
    if (POSTAL_RE.test(t)) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    clean.push(t);
  }
  if (clean.length === 0) return { primary: full, context: [], full };
  if (clean.length === 1) return { primary: clean[0], context: [], full };

  const primary = clean[0];
  const tail = clean.slice(1);
  // Context compatto: gli ultimi MAX_CONTEXT token (tipicamente città/regione +
  // paese). I livelli intermedi rumorosi (via, municipio, …) restano esclusi.
  const context = tail.slice(Math.max(0, tail.length - MAX_CONTEXT));
  return { primary, context, full };
}
